import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { useVirtualizer } from '@tanstack/react-virtual';
import { createPortal } from 'react-dom';
import { 
  Database, Plus, Calendar, Clock, ChevronRight, AlertCircle, Trash2, CheckCircle2,
  Layers, Search, ArrowRight, MoreVertical, X, ChevronLeft, Edit3, Check, RotateCcw, 
  MessageSquare, Bug, Zap, Info, Globe, Link as LinkIcon, ExternalLink, Code2, RefreshCw,
  Activity, Wifi, WifiOff, Filter, Loader2
} from 'lucide-react';
import FilterModal from '../components/FilterModal';
import Skeleton from '../components/Skeleton';
import { io } from 'socket.io-client';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import CustomDropdown from '../components/CustomDropdown';
import SlideToConfirm from '../components/SlideToConfirm';
import ConfirmationModal from '../components/ConfirmationModal';
import Tooltip from '../components/Tooltip';
import EmptyState from '../components/EmptyState';

const springTransition = { type: "spring", stiffness: 350, damping: 30, mass: 1 };

const CopyableBadge = ({ text, styles }) => {
  const [copied, setCopied] = useState(false);
  const { t } = useLanguage();
  
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.span 
      onClick={handleCopy}
      whileTap={{ scale: 0.95 }}
      className="px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest border cursor-pointer select-none transition-colors relative h-5 flex items-center justify-center overflow-hidden" 
      style={{ 
        backgroundColor: copied ? '#10b981' : styles.bg, 
        color: copied ? 'white' : styles.text, 
        borderColor: copied ? '#059669' : styles.border 
      }}
    >
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.span 
            key="copied"
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            className="flex items-center gap-1"
          >
            <Check size={8} strokeWidth={3} />
            {t('copied')}
          </motion.span>
        ) : (
          <motion.span
            key="id"
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
          >
            {text}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.span>
  );
};
 
const BacklogItem = React.memo(({ ticket, getPriorityStyles, setSelectedTicket, selectedSprintId, selectedSprint, addToSprint, handleDeleteTicket, t, lang }) => {
  const ps = getPriorityStyles(ticket.priority || 'medium');
  const isPlanned = (ticket.status === 'bug_fixing' || ticket.type === 'bug' || ticket.originalStatus === 'bug_fixing') || (ticket.planning && typeof ticket.planning.score === 'number');

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -2 }}
      onClick={() => setSelectedTicket(ticket)} 
      className={`pl-7 pr-4 py-4 rounded-[20px] border flex flex-col gap-4 group transition-all cursor-pointer ${!isPlanned ? 'opacity-80' : ''}`} 
      style={{ backgroundColor: ps.bgSubtle, borderColor: ps.borderSubtle }}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="p-2.5 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: ps.bgSubtle, color: ps.text }}>
            {(ticket.status === 'bug_fixing' || ticket.type === 'bug' || ticket.originalStatus === 'bug_fixing') ? <Bug size={18} /> : (isPlanned ? <AlertCircle size={18} /> : <Zap size={18} className="text-slate-500" />)}
          </div>
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 shrink-0">
                <CopyableBadge 
                  text={ticket.key || `#${ticket._id.slice(-6).toUpperCase()}`} 
                  styles={{ bg: ps.bg, text: ps.text, border: ps.border }} 
                />
                {ticket.planning?.score ? (
                  <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-500 text-[10px] font-black border border-amber-500/20 shadow-sm shadow-amber-500/5">
                    <Zap size={10} fill="currentColor" />
                    {ticket.planning.score}
                  </div>
                ) : (
                  !isPlanned && (
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/5 text-slate-500 text-[8px] font-black border border-white/10 uppercase tracking-[0.1em]">
                       <Database size={8} />
                       {lang === 'es' ? 'Pendiente Planificar' : 'Awaiting Planning'}
                    </div>
                  )
                )}
              </div>
              <h4 className="font-bold text-slate-100 text-sm line-clamp-1">{ticket.title || ticket.message}</h4>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider opacity-40">
                <Clock size={10} />
                {new Date(ticket.createdAt).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="flex items-center gap-2 ml-2">
                <p className="text-[9px] uppercase font-black tracking-widest flex items-center gap-1 opacity-60" style={{ color: ps.text }}>
                   {t('priority')}: {t(ticket.priority || 'medium')}
                   {ticket.planning?.priority && (
                     <span className="text-slate-500 lowercase opacity-60">({Number(ticket.planning.priority).toFixed(2)})</span>
                   )}
                </p>
              </div>
              {ticket.isSynced && (
                <span className="flex items-center gap-1 text-[8px] font-black uppercase text-blue-400 opacity-60">
                  <RefreshCw size={8} />
                  {ticket.origin || 'sync'}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {selectedSprintId && (selectedSprint?.status === 'planned' || selectedSprint?.status === 'draft') && (
            <button 
              onClick={(e) => { e.stopPropagation(); addToSprint(ticket._id); }} 
              className="p-2 rounded-xl bg-primary text-white hover:bg-blue-600 transition-all shadow-lg"
            >
              <Plus size={16} />
            </button>
          )}
          <button onClick={(e) => handleDeleteTicket(ticket._id, ticket.title || ticket.message, ticket.key || `#${ticket._id.slice(-6).toUpperCase()}`, e)} className="p-2 rounded-xl bg-white/5 text-slate-500 hover:bg-rose-500/10 hover:text-rose-500 transition-all">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );
});

const BacklogItemSkeleton = React.memo(() => (
  <div className="pl-7 pr-4 py-4 rounded-[20px] border border-white/5 bg-white/[0.01] flex flex-col gap-4 mb-3">
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-4 flex-1">
        <Skeleton width="40px" height="40px" borderRadius="0.75rem" />
        <div className="flex flex-col gap-2 flex-1">
          <div className="flex items-center gap-3">
            <Skeleton width="60px" height="16px" borderRadius="0.375rem" />
            <Skeleton width="200px" height="16px" borderRadius="0.375rem" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton width="80px" height="8px" />
            <Skeleton width="60px" height="8px" />
          </div>
        </div>
      </div>
      <Skeleton width="32px" height="32px" borderRadius="0.75rem" />
    </div>
  </div>
));

const SprintSkeleton = () => (
  <div className="w-full text-left p-4 rounded-2xl border border-white/5 bg-white/[0.02]">
    <div className="flex items-center justify-between mb-3">
      <Skeleton width="120px" height="16px" />
      <Skeleton width="60px" height="18px" borderRadius="0.25rem" />
    </div>
    <div className="flex items-center gap-2">
      <Skeleton width="12px" height="12px" borderRadius="50%" />
      <Skeleton width="150px" height="10px" />
    </div>
  </div>
);

const SyncModal = ({ isOpen, onClose, onSync, isSyncing, hasJira, hasAzure, t }) => {
  if (!isOpen) return null;
  return createPortal(
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        className="fixed inset-0 z-[10000] flex items-center justify-center p-6"
      >
        <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-xl" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-sm bg-slate-900 border border-white/10 rounded-[32px] p-8 shadow-3xl text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mx-auto mb-6">
            <RefreshCw size={32} className={isSyncing ? 'animate-spin' : ''} />
          </div>
          <h3 className="text-2xl font-black text-white mb-2 italic">{t('syncToExternal')}</h3>
          <p className="text-slate-500 text-sm mb-8 leading-relaxed font-medium">{t('selectIntegration')}</p>
          
          <div className="grid gap-3 mb-8">
            {hasJira && (
              <button 
                onClick={() => onSync('jira')}
                disabled={isSyncing}
                className="w-full flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:border-primary/50 transition-all group"
              >
                <div className="flex items-center gap-3">
                   <div className="w-8 h-8 rounded-lg bg-[#0052CC]/10 text-[#0052CC] flex items-center justify-center border border-[#0052CC]/20 group-hover:bg-[#0052CC] group-hover:text-white transition-all"><Globe size={16} /></div>
                   <span className="font-bold text-slate-200">Jira Cloud</span>
                </div>
                <ChevronRight size={16} className="text-slate-600 group-hover:text-primary" />
              </button>
            )}
            {hasAzure && (
              <button 
                onClick={() => onSync('azure')}
                disabled={isSyncing}
                className="w-full flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:border-[#0078D4]/50 transition-all group"
              >
                <div className="flex items-center gap-3">
                   <div className="w-8 h-8 rounded-lg bg-[#0078D4]/10 text-[#0078D4] flex items-center justify-center border border-[#0078D4]/20 group-hover:bg-[#0078D4] group-hover:text-white transition-all"><Code2 size={16} /></div>
                   <span className="font-bold text-slate-200">Azure DevOps</span>
                </div>
                <ChevronRight size={16} className="text-slate-600 group-hover:text-[#0078D4]" />
              </button>
            )}
          </div>

          <button
            onClick={onClose}
            className="w-full py-4 text-slate-500 font-bold hover:text-white transition-all"
          >
            {t('cancel')}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

const getSprintDays = (sprint) => {
  if (!sprint) return 0;
  if (sprint.duration) return parseInt(sprint.duration);
  const start = new Date(sprint.startDate);
  const end = new Date(sprint.endDate);
  const diffTime = Math.abs(end - start);
  if (isNaN(diffTime)) return 0;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

const getSprintCapacity = (sprint, pointsPerDay = 5) => {
  if (!sprint) return 0;
  if (sprint.capacity) return sprint.capacity;
  return getSprintDays(sprint) * pointsPerDay;
};

const getPriorityStyles = (priority) => {
  switch (priority) {
    case 'urgent':
      return {
        bg: '#fef2f2',
        text: '#b91c1c',
        border: '#fee2e2',
        bgHover: '#b91c1c',
        bgSubtle: 'rgba(185, 28, 28, 0.1)',
        borderSubtle: 'rgba(185, 28, 28, 0.2)',
        textSubtle: '#fca5a5'
      };
    case 'high':
      return {
        bg: '#fffbeb',
        text: '#b45309',
        border: '#fef3c7',
        bgHover: '#b45309',
        bgSubtle: 'rgba(180, 83, 9, 0.1)',
        borderSubtle: 'rgba(180, 83, 9, 0.2)',
        textSubtle: '#fdba74'
      };
    case 'medium':
      return {
        bg: '#eef2ff',
        text: '#4338ca',
        border: '#e0e7ff',
        bgHover: '#4338ca',
        bgSubtle: 'rgba(67, 56, 202, 0.1)',
        borderSubtle: 'rgba(67, 56, 202, 0.2)',
        textSubtle: '#a5b4fc'
      };
    case 'low':
      return {
        bg: '#ecfdf5',
        text: '#059669',
        border: '#d1fae5',
        bgHover: '#059669',
        bgSubtle: 'rgba(5, 150, 105, 0.1)',
        borderSubtle: 'rgba(5, 150, 105, 0.2)',
        textSubtle: '#6ee7b7'
      };
    default: // medium
      return {
        bg: '#eef2ff',
        text: '#4338ca',
        border: '#e0e7ff',
        bgHover: '#4338ca',
        bgSubtle: 'rgba(67, 56, 202, 0.1)',
        borderSubtle: 'rgba(67, 56, 202, 0.2)',
        textSubtle: '#a5b4fc'
      };
  }
};

const getStatusStyles = (status) => {
  switch (status) {
    case 'todo': return 'bg-amber-500/5 border-amber-500/10 hover:border-amber-500/30';
    case 'review': return 'bg-purple-500/5 border-purple-500/10 hover:border-purple-500/30';
    case 'done': return 'bg-emerald-500/5 border-emerald-500/10 hover:border-emerald-500/30';
    case 'bug_fixing': return 'bg-rose-500/5 border-rose-500/10 hover:border-rose-500/30';
    case 'ready_for_review': return 'bg-purple-500/5 border-purple-500/10 hover:border-purple-500/30';
    default: return 'bg-slate-500/5 border-slate-500/10 hover:border-slate-500/30';
  }
};

const getStatusHex = (status) => {
  switch (status) {
    case 'todo': return '#f59e0b';
    case 'review': case 'ready_for_review': return '#a855f7';
    case 'done': case 'completed': return '#10b981';
    case 'bug_fixing': return '#f43f5e';
    case 'backlog': return '#64748b';
    default: return '#64748b';
  }
};


const TicketModal = ({ ticket, onClose, onUpdate, onAddToSprint, activeSprint, t }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState(ticket.description || '');
  const [editedPriority, setEditedPriority] = useState(ticket.priority || 'medium');
  const [isSaving, setIsSaving] = useState(false);
  const { lang } = useLanguage();
  const pStyles = getPriorityStyles(editedPriority);

  if (!ticket || typeof document === 'undefined') return null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await api.patch(`/tickets/${ticket._id}`, { 
        description: editedDescription,
        priority: editedPriority 
      });
      const updatedData = response.data || { ...ticket, description: editedDescription, priority: editedPriority };
      if (onUpdate) onUpdate(updatedData);
      setIsEditing(false);
    } catch (err) { /* Silent fail */ } finally { setIsSaving(false); }
  };

  const handlePriorityChange = async (newPriority) => {
    setEditedPriority(newPriority);
    try {
      const response = await api.patch(`/tickets/${ticket._id}`, {
        priority: newPriority
      });
      if (onUpdate) {
        onUpdate(response.data || { ...ticket, priority: newPriority });
      }
    } catch (err) {
      /* Silent error handling for production console hygiene */
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-slate-950/80 backdrop-blur-3xl" />
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", stiffness: 450, damping: 35 }}
        className="relative w-full max-w-2xl border rounded-[24px] overflow-visible shadow-2xl flex flex-col max-h-[80vh] backdrop-blur-xl" 
        style={{ 
          backgroundColor: pStyles.bgSubtle,
          borderColor: pStyles.borderSubtle,
          boxShadow: `0 10px 20px -5px rgba(0,0,0,0.5)`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 pb-4 flex justify-between items-start border-b border-white/5 bg-white/[0.01]">
          <div className="space-y-4 flex-1">
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 rounded-lg border text-[10px] font-black uppercase tracking-widest" style={{ backgroundColor: pStyles.bg, color: pStyles.text, borderColor: pStyles.border }}>{ticket.key || `#${ticket._id.slice(-6).toUpperCase()}`}</span>
              <CustomDropdown 
                 className="w-32 scale-90 origin-left"
                 solid={true}
                 backgroundColor={getStatusHex(ticket.status)}
                 options={[
                   { value: 'urgent', label: t('urgent'), icon: AlertCircle, activeColor: '#ef4444' },
                   { value: 'high', label: t('high'), icon: AlertCircle, activeColor: '#f59e0b' },
                   { value: 'medium', label: t('medium'), icon: Clock, activeColor: '#6366f1' },
                   { value: 'low', label: t('low'), icon: CheckCircle2, activeColor: '#10b981' }
                 ]}
                 value={editedPriority}
                  onChange={handlePriorityChange}
                  placeholder={t('priority')}
                  showHeader={false}
                />
            </div>
            <h2 className="text-2xl font-black text-white">{ticket.title || ticket.message}</h2>
            
            {ticket.focusAreas && ticket.focusAreas.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {ticket.focusAreas.includes('backend') && (
                  <span className="px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                    <Globe size={11} /> Backend
                  </span>
                )}
                {ticket.focusAreas.includes('frontend') && (
                  <span className="px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                    <Layers size={11} /> Frontend
                  </span>
                )}
                {ticket.focusAreas.includes('ux') && (
                  <span className="px-2.5 py-1 rounded-lg bg-fuchsia-500/10 text-fuchsia-400 border border-fuchsia-500/20 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                    <LinkIcon size={11} /> UX/UI
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-2.5 rounded-xl bg-white/5 text-slate-400 hover:text-red-500 hover:bg-red-500/10 transition-all border border-white/10"><X size={20} /></button>
          </div>
        </div>

        {/* Horizontal Metadata - Stitch Style */}
        <div className="px-8 py-3 bg-white/[0.02] border-b border-white/5 flex flex-wrap items-center gap-6 flex-none">
           <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500/30 to-purple-500/30 border border-white/10 flex items-center justify-center text-[9px] text-white font-black overflow-hidden shadow-lg shadow-indigo-500/10">
                 {ticket.creator?.avatar ? (
                    <img src={ticket.creator.avatar} className="w-full h-full object-cover" alt="" />
                 ) : (
                    ticket.creator?.name?.[0] || 'A'
                 )}
              </div>
              <div className="flex flex-col">
                 <span className="text-[9px] font-black text-white uppercase tracking-[0.15em] leading-none mb-1">{t('owner')}</span>
                 <span className="text-[10px] font-bold text-slate-200 leading-none">{ticket.creator?.name || t('admin')}</span>
              </div>
           </div>

           <div className="h-4 w-px bg-white/5" />

           {ticket.resolver && (
             <>
               <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500/30 to-teal-500/30 border border-white/10 flex items-center justify-center text-[9px] text-white font-black overflow-hidden shadow-lg shadow-emerald-500/10">
                     {ticket.resolver.avatar ? (
                        <img src={ticket.resolver.avatar} className="w-full h-full object-cover" alt="" />
                     ) : (
                        ticket.resolver.name?.[0] || 'R'
                     )}
                  </div>
                  <div className="flex flex-col">
                     <span className="text-[9px] font-black text-white uppercase tracking-[0.15em] leading-none mb-1">{ticket.sprintId ? t('resolver') : t('plannedBy')}</span>
                     <span className="text-[10px] font-bold text-slate-200 leading-none truncate max-w-[80px]">{ticket.resolver.name}</span>
                  </div>
               </div>
               <div className="h-4 w-px bg-white/5" />
             </>
           )}

           <div className="flex flex-col">
              <span className="text-[9px] font-black text-white uppercase tracking-[0.15em] leading-none mb-1">{t('created')}</span>
              <span className="text-[10px] font-bold text-slate-200 leading-none">
                {new Date(ticket.createdAt).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
           </div>
        </div>

        <div className="p-8 flex-1 overflow-y-auto min-h-0 space-y-8 custom-scrollbar">
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                <Edit3 size={14} />
                {t('description')}
              </h3>
              {!isEditing ? (
                <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 text-xs font-bold transition-all px-3 py-1.5 rounded-lg border" style={{ color: pStyles.text, borderColor: pStyles.borderSubtle, backgroundColor: pStyles.bgSubtle }}>
                  <Edit3 size={12} />
                  {t('edit')}
                </button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={handleSave} disabled={isSaving} className="flex items-center gap-2 text-xs font-black px-4 py-1.5 rounded-lg shadow-lg border transition-all" style={{ backgroundColor: pStyles.text, borderColor: pStyles.borderSubtle, color: 'white' }}>
                    {isSaving ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <RotateCcw size={14} />}
                    {t('save')}
                  </button>
                  <button onClick={() => {
                    setIsEditing(false);
                    setEditedDescription(ticket.description || '');
                    setEditedPriority(ticket.priority || 'medium');
                  }} className="text-xs font-bold text-slate-500 hover:text-white px-3 py-1.5 rounded-lg border border-white/10">{t('cancel')}</button>
                </div>
              )}
            </div>
            {isEditing ? (
              <textarea 
                autoFocus 
                id="ticket-description-edit"
                name="description"
                value={editedDescription} 
                onChange={(e) => setEditedDescription(e.target.value)} 
                className="w-full h-48 bg-white/[0.02] border rounded-2xl p-6 text-slate-300 text-sm leading-relaxed focus:outline-none transition-all resize-none" 
                style={{ borderColor: pStyles.borderSubtle }}
                placeholder={t('descriptionPlaceholder')} 
              />
            ) : (
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
                <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">{ticket.description || t('noDescription')}</p>
              </div>
            )}
          </section>

          {(ticket.backendEndpoint || ticket.figmaReference) && (
            <section className="space-y-4 pt-4 border-t border-white/5">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                <Zap size={14} />
                {t('focusDetails')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(ticket.backendEndpoints?.length > 0 || ticket.backendEndpoint) && (
                  <div className={(ticket.figmaReference) ? "glass-card p-4 border-white/5 space-y-2" : "glass-card p-4 border-white/5 space-y-2 col-span-full"}>
                    <div className="flex items-center gap-2 text-[10px] font-black text-blue-400 uppercase tracking-tighter">
                      <Globe size={12} /> Backend Context
                    </div>
                    <div className="space-y-2">
                      {ticket.backendEndpoints?.length > 0 ? (
                        ticket.backendEndpoints.map((endpoint, idx) => (
                          <code key={idx} className="block text-[10px] font-mono font-bold text-slate-200 bg-white/5 p-2 rounded truncate border border-white/5">
                            {endpoint}
                          </code>
                        ))
                      ) : (
                        <code className="block text-[10px] font-mono font-bold text-slate-200 bg-white/5 p-2 rounded truncate">
                          {ticket.backendEndpoint}
                        </code>
                      )}
                    </div>
                  </div>
                )}
                {ticket.figmaReference && (
                  <div className={(ticket.backendEndpoints?.length > 0 || ticket.backendEndpoint) ? "glass-card p-4 border-white/5 space-y-4" : "glass-card p-4 border-white/5 space-y-4 col-span-full"}>
                    <div className="flex items-center gap-2 text-[10px] font-black text-fuchsia-400 uppercase tracking-widest">
                      <div className="w-5 h-5 rounded-lg bg-fuchsia-500/10 flex items-center justify-center">
                        <LinkIcon size={10} />
                      </div>
                      {t('uxReferences')}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Array.isArray(ticket.figmaReference) ? (
                        ticket.figmaReference.map((ref, idx) => (
                          <motion.a 
                            key={idx}
                            href={ref.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            whileHover={{ x: 4 }}
                            className="group/link flex items-center justify-between p-3 rounded-2xl bg-white/[0.03] border border-white/5 hover:bg-fuchsia-500/[0.04] hover:border-fuchsia-500/20 transition-all cursor-pointer relative overflow-hidden"
                          >
                            <div className="absolute inset-y-0 left-0 w-1 bg-fuchsia-500 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                            <div className="flex flex-col min-w-0 pr-4">
                              <span className="text-[8px] text-fuchsia-400 uppercase font-black tracking-widest mb-0.5 group-hover/link:translate-x-1 transition-transform duration-300">{ref.name}</span>
                              <span className="text-slate-200 text-[10px] font-bold truncate max-w-[180px] group-hover/link:text-white transition-colors">{ref.link}</span>
                            </div>
                            <div className="shrink-0 p-1.5 rounded-xl bg-white/5 text-slate-500 group-hover/link:bg-fuchsia-500 group-hover/link:text-white transition-all duration-300">
                               <ExternalLink size={10} />
                            </div>
                          </motion.a>
                        ))
                      ) : (
                        <a 
                          href={ticket.figmaReference} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group/link flex items-center justify-between p-3 rounded-2xl bg-white/[0.03] border border-white/5 hover:bg-fuchsia-500/[0.04] hover:border-fuchsia-500/20 transition-all cursor-pointer relative overflow-hidden"
                        >
                           <div className="absolute inset-y-0 left-0 w-1 bg-fuchsia-500 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                           <div className="flex flex-col min-w-0 pr-4">
                              <span className="text-[8px] text-fuchsia-400 uppercase font-black tracking-widest mb-0.5">{t('visualReference')}</span>
                              <span className="text-slate-200 text-[10px] font-bold truncate max-w-[180px]">Figma Design Link</span>
                           </div>
                           <div className="shrink-0 p-1.5 rounded-xl bg-white/5 text-slate-500 group-hover/link:bg-fuchsia-500 group-hover/link:text-white transition-all">
                              <ExternalLink size={10} />
                           </div>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {ticket.aiFixSummary && (
            <section className="space-y-4 pt-4 border-t border-white/5">
              <div className="p-6 rounded-2xl bg-emerald-500/5 border border-emerald-500/10 relative overflow-hidden group/fix">
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 size={14} className="text-emerald-400" />
                    <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-widest">
                      {t('aiFixSummary')}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    {ticket.aiFixSummary}
                  </p>
                </div>
              </div>
            </section>
          )}

          {ticket.originalComment && (
            <section className="space-y-4 pt-4 border-t border-white/5">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                <MessageSquare size={14} />
                {t('originalComment')}
              </h3>
              <div className="p-6 rounded-2xl bg-white/[0.01] border border-white/5">
                <p className="text-xs text-slate-500 leading-relaxed italic">"{ticket.originalComment}"</p>
              </div>
            </section>
          )}

          {/* Planning Section */}
          {ticket.planning && ticket.planning.complejidad && (
            <section className="space-y-4 pt-8 border-t border-white/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap size={14} className="text-amber-400" />
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
                    {t('planningAI')}
                    {ticket.planning.tipo && (
                      <Tooltip content={t('typeTooltip')}>
                        <span 
                          className="ml-2 px-2 py-0.5 rounded-md text-[8px] font-black bg-white/5 border border-white/5 text-slate-400 cursor-help flex items-center gap-1 transition-colors hover:bg-white/10"
                        >
                          {t(ticket.planning.tipo.toLowerCase().replace(/-(.)/g, (m, c) => c.toUpperCase()).replace(/^ai/, 'ai'))}
                          <Info size={8} />
                        </span>
                      </Tooltip>
                    )}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                   <Tooltip content={t('scoreTooltip')}>
                     <div 
                       className="flex flex-col items-end cursor-help group/tip"
                     >
                        <span className="text-[8px] font-black uppercase text-slate-600 tracking-tighter mb-0.5 flex items-center gap-1">
                          {t('score')}
                          <Info size={8} className="opacity-0 group-hover/tip:opacity-100 transition-opacity" />
                        </span>
                        <span className="text-xs font-black px-2 py-1 rounded-lg bg-primary/20 text-primary border border-primary/20 group-hover/tip:border-primary/40 transition-colors">
                          {ticket.planning.score}
                        </span>
                     </div>
                   </Tooltip>
                   <Tooltip content={t('priorityTooltip')}>
                     <div 
                       className="flex flex-col items-end ml-2 cursor-help group/tip"
                     >
                        <span className="text-[8px] font-black uppercase text-slate-600 tracking-tighter mb-0.5 flex items-center gap-1">
                          {t('priority')}
                          <Info size={8} className="opacity-0 group-hover/tip:opacity-100 transition-opacity" />
                        </span>
                        <span className="text-xs font-black px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 group-hover/tip:border-emerald-500/40 transition-colors">
                          {Number(ticket.planning.priority).toFixed(2)}
                        </span>
                     </div>
                   </Tooltip>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: t('complexity'), value: ticket.planning.complejidad, color: '#6366f1', tooltip: t('complexityTooltip') },
                  { label: t('uncertainty'), value: ticket.planning.incertidumbre, color: '#a855f7', tooltip: t('uncertaintyTooltip') },
                  { label: t('impact'), value: ticket.planning.impacto, color: '#f59e0b', tooltip: t('impactTooltip') },
                  { label: t('risk'), value: ticket.planning.riesgo, color: '#f43f5e', tooltip: t('riskTooltip') }
                ].map((item, idx) => (
                  <Tooltip key={idx} content={item.tooltip}>
                    <div 
                      className="p-4 rounded-[20px] bg-white/[0.02] border border-white/5 flex flex-col gap-2 group/metric hover:bg-white/[0.04] transition-all cursor-help w-full"
                    >
                      <span className="text-[8px] font-black uppercase text-slate-500 tracking-[0.1em] flex items-center justify-between">
                        {item.label}
                        <Info size={10} className="opacity-20 group-hover/metric:opacity-100 transition-opacity" />
                      </span>
                      <div className="flex items-end justify-between gap-2">
                         <span className="text-xl font-black text-white leading-none">{item.value}</span>
                         <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden mb-1">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${(item.value / 3) * 100}%` }}
                              className="h-full rounded-full" 
                              style={{ backgroundColor: item.color }} 
                            />
                         </div>
                      </div>
                    </div>
                  </Tooltip>
                ))}
              </div>

              {ticket.planning.aiAnalysis && (
                <div className="p-6 rounded-[20px] bg-primary/5 border border-primary/10 relative overflow-hidden group/analysis">
                   <div className="relative z-10">
                     <p className="text-[13px] text-slate-300 leading-relaxed font-medium">
                       <span className="font-black text-primary mr-2 uppercase text-[10px] tracking-widest font-mono italic">{t('aiAnalysis')}:</span>
                       {ticket.planning.aiAnalysis}
                     </p>
                   </div>
                   <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 blur-[50px] rounded-full translate-x-16 -translate-y-16 group-hover/analysis:bg-primary/20 transition-all duration-700" />
                </div>
              )}

              {onAddToSprint && !ticket.sprintId && activeSprint && (
                <div className="pt-4">
                  <button 
                    onClick={() => onAddToSprint(ticket._id)}
                    className="w-full py-5 text-white rounded-[24px] text-xs font-black uppercase tracking-[0.2em] transition-all shadow-2xl active:scale-[0.98] border border-white/10 flex items-center justify-center gap-3"
                    style={{ 
                      backgroundColor: pStyles.text,
                      boxShadow: `0 20px 40px -12px ${pStyles.text}40`
                    }}
                  >
                    <Plus size={18} strokeWidth={3} />
                    {t('addToSprint')} {activeSprint.name}
                  </button>
                </div>
              )}
            </section>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

import CustomDatePicker from '../components/CustomDatePicker';

const fetcher = url => api.get(url).then(res => res.data);

const Backlog = () => {
  const { t, lang } = useLanguage();
  const { activeProjectId, activeProject, filteredProjects, canAccess, isLoading: isLoadingProjects, isInitialized } = useProject();
  const { token, user } = useAuth();
  const { addToast } = useToast();
  const SWR_STALE_CONFIG = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 2000,
  };
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- STATE DECLARATIONS (Moved up to avoid ReferenceError) ---
  const [selectedSprintId, setSelectedSprintId] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newSprint, setNewSprint] = useState({ name: '', duration: '14', startDate: new Date().toISOString().split('T')[0] });
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [isCreating, setIsCreating] = useState(false);
  const [assigningTicketId, setAssigningTicketId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [serverSearchTerm, setServerSearchTerm] = useState('');
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [isDeletingSprint, setIsDeletingSprint] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [activeTab, setActiveTab] = useState('backlog');
  const [confModal, setConfModal] = useState({ isOpen: false, onConfirm: () => {}, title: '', message: '', type: 'danger' });
  const [realTimeStatus, setRealTimeStatus] = useState('connecting');
  const [backlogFilters, setBacklogFilters] = useState({ priority: 'all', planning: 'all' });
  const [sprintFilters, setSprintFilters] = useState({ priority: 'all', status: 'all' });
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const listRef = useRef(null);
  const sprintListRef = useRef(null);
  const loadMoreRef = useRef(null);
  const sprintLoadMoreRef = useRef(null);
  const priorityLevels = useMemo(() => ({ urgent: 0, high: 1, medium: 2, low: 3 }), []);
  const getSorted = useCallback((tt) => (tt || []).sort((a, b) => (priorityLevels[a.priority] ?? 2) - (priorityLevels[b.priority] ?? 2)), [priorityLevels]);
  const POINTS_PER_DAY = activeProject?.settings?.pointsPerDay || 5;

  // --- PAGINATED DATA LAYER ---
  const getInfiniteKey = (pageIndex, previousPageData) => {
    if (!activeProjectId) return null;
    if (previousPageData && !previousPageData.data?.length) return null;

    let url = `/tickets?projectId=${activeProjectId}&sprintId=null&page=${pageIndex}&limit=10`;
    if (serverSearchTerm) url += `&search=${encodeURIComponent(serverSearchTerm)}`;
    if (backlogFilters.priority !== 'all') url += `&priority=${backlogFilters.priority}`;
    if (backlogFilters.type && backlogFilters.type !== 'all') url += `&type=${backlogFilters.type}`;
    
    if (backlogFilters.planning !== 'all') {
      if (backlogFilters.planning === 'bug') url += `&status=bug`;
      else url += `&planningStatus=${backlogFilters.planning}`;
    }

    return url;
  };

  const { 
    data: infiniteData, 
    mutate: mutateTickets, 
    size, 
    setSize, 
    isLoading: isLoadingTickets,
    isValidating: swrValidating
  } = useSWRInfinite(getInfiniteKey, fetcher, { 
    ...SWR_STALE_CONFIG,
    revalidateFirstPage: false,
    persistSize: true
  });

  const { data: sprintsData = [], mutate: mutateSprints, isLoading: isLoadingSprints } = useSWR(activeProjectId ? `/projects/${activeProjectId}/sprints` : null, fetcher, SWR_STALE_CONFIG);
  const sprints = Array.isArray(sprintsData?.data) ? sprintsData.data : (Array.isArray(sprintsData) ? sprintsData : []);

  // Dedicated INFINITE fetcher for the selected sprint to ensure performance on large sprints
  const getSprintInfiniteKey = (pageIndex, previousPageData) => {
    if (!activeProjectId || !selectedSprintId) return null;
    if (previousPageData && !previousPageData.data?.length) return null;

    let url = `/tickets?projectId=${activeProjectId}&sprintId=${selectedSprintId}&page=${pageIndex}&limit=10`;
    if (serverSearchTerm) url += `&search=${encodeURIComponent(serverSearchTerm)}`;
    if (sprintFilters.priority !== 'all') url += `&priority=${sprintFilters.priority}`;
    if (sprintFilters.type && sprintFilters.type !== 'all') url += `&type=${sprintFilters.type}`;
    
    if (sprintFilters.status !== 'all') {
      const s = sprintFilters.status;
      if (s === 'bug') url += `&status=bug`;
      else if (s === 'pending') url += `&status=pending`;
      else if (s === 'review') url += `&status=ready_for_review`;
      else if (s === 'completed') url += `&status=completed`;
    }

    return url;
  };

  const { 
    data: sprintInfiniteData, 
    mutate: mutateSelectedSprint,
    size: sprintSize,
    setSize: setSprintSize,
    isValidating: sprintSwrValidating,
    isLoading: isLoadingSprint
  } = useSWRInfinite(getSprintInfiniteKey, fetcher, {
    ...SWR_STALE_CONFIG,
    revalidateFirstPage: false,
    persistSize: true
  });

  const selectedSprintTickets = useMemo(() => {
    if (!sprintInfiniteData) return [];
    const seen = new Set();
    const tickets = sprintInfiniteData.flatMap(page => page.data || []);
    return tickets.filter(ticket => {
      if (!ticket?._id || seen.has(ticket._id)) return false;
      seen.add(ticket._id);
      return true;
    });
  }, [sprintInfiniteData]);

  const allTickets = useMemo(() => {
    if (!infiniteData) return [];
    const seen = new Set();
    const tickets = infiniteData.flatMap(page => page.data || []);
    return tickets.filter(ticket => {
      if (!ticket?._id || seen.has(ticket._id)) return false;
      seen.add(ticket._id);
      return true;
    });
  }, [infiniteData]);

  const isLoading = (isLoadingSprints || (selectedSprintId && sprintSwrValidating && !selectedSprintTickets.length)) && !!activeProjectId;
  const isInitialLoading = isLoading && !sprints.length && !serverSearchTerm;
  const isSearching = swrValidating && searchQuery.length > 2;

  const { data: stats } = useSWR(activeProjectId ? `/stats/team/${activeProjectId}` : null, fetcher, {
    refreshInterval: 30000
  });

  // Dedicated endpoint for high-performance dashboard counts (Backlog & Sprint totals)
  const { data: dashboardCounts, mutate: mutateCounts } = useSWR(
    activeProjectId ? `/projects/${activeProjectId}/dashboard/counts?sprintId=${selectedSprintId || ''}` : null, 
    fetcher
  );

  // Debounced mutation handler to prevent over-rendering during bursts of events
  const debouncedMutate = useMemo(() => {
    let timeout;
    return () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        mutateTickets();
        mutateSelectedSprint();
        mutateSprints();
        mutateCounts();
      }, 300);
    };
  }, [mutateTickets, mutateSelectedSprint, mutateSprints, mutateCounts]);

  const mutateTicketsRef = useRef(mutateTickets);
  const mutateSelectedSprintRef = useRef(mutateSelectedSprint);
  useEffect(() => {
    mutateTicketsRef.current = mutateTickets;
    mutateSelectedSprintRef.current = mutateSelectedSprint;
  }, [mutateTickets, mutateSelectedSprint]);

  const socketRef = useRef(null);

  // Real-time Socket.io Integration
  useEffect(() => {
    if (!activeProjectId || !token) return;

    const socketUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:4000';
    
    const socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setRealTimeStatus('connected');
      socket.emit('join-project', activeProjectId);
    });

    socket.on('annotation-mutated', (data) => {
      if (data && data.ticket) {
        const mutateFn = (current) => {
          if (!current || !Array.isArray(current)) return current;
          return current.map(page => {
            if (data.type === 'update') {
              return { 
                ...page, 
                data: (page.data || []).map(t => t._id === data.id ? { ...t, ...data.ticket } : t) 
              };
            }
            if (data.type === 'delete') {
              return { 
                ...page, 
                data: (page.data || []).filter(t => t._id !== data.id) 
              };
            }
            return page;
          });
        };

        mutateTicketsRef.current(mutateFn, false);
        mutateSelectedSprintRef.current(mutateFn, false);

        if (data.type === 'create') {
          debouncedMutate();
        }
      } else {
        debouncedMutate();
      }
    });

    socket.on('sprint-update', (data) => {
      debouncedMutate();
    });

    socket.on('connect_error', (err) => {
      setRealTimeStatus('disconnected');
    });

    socket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect' || reason === 'transport close') {
        setRealTimeStatus('disconnected');
      }
    });

    return () => {
      if (socket) {
        socket.off();
        socket.disconnect();
      }
    };
  }, [activeProjectId, token]); // STABLE: Only depends on project and auth

  useEffect(() => {
    if (isInitialized && !isLoadingProjects && !canAccess('/backlog')) {
      navigate('/projects');
    }
  }, [canAccess, navigate, isLoadingProjects, isInitialized]);

  const hasJira = activeProject?.settings?.integrations?.jira?.enabled;
  const hasAzure = activeProject?.settings?.integrations?.azure?.enabled;

  const handleSyncToExternal = async (provider) => {
    setIsSyncing(true);
    try {
      const ticketsToSync = selectedSprintId ? sortedSprintTickets : filteredBacklog.filter(t => !t.sprintId);
      
      if (ticketsToSync.length === 0) {
        addToast(t('noTicketsToSync'), 'warning');
        setIsSyncing(false);
        return;
      }
      
      await api.post(`/projects/${activeProjectId}/integrations/${provider}/sync`, {
        ticketIds: ticketsToSync.map(t => t._id)
      });
      
      addToast(t('syncSuccess'), 'success');
      setShowSyncModal(false);
    } catch (err) {
      addToast(t('error'), 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  // Sync search from URL params if changed externally
  useEffect(() => {
    const search = searchParams.get('search');
    if (search && search !== searchQuery) {
      setSearchQuery(search);
    }
  }, [searchParams]);

  // Smooth scroll to top when modal opens
  useEffect(() => {
    if (selectedTicket || isCreateModalOpen || confModal.isOpen) {
      const mainElement = document.querySelector('main');
      if (mainElement) {
        mainElement.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [selectedTicket, isCreateModalOpen, confModal.isOpen]);



  // Reset relevant state when project changes to prevent race conditions or mismatched IDs
  useEffect(() => {
    if (activeProjectId) {
      setSelectedSprintId(null);
      setServerSearchTerm('');
      setSearchQuery('');
      setSelectedTicket(null);
    }
  }, [activeProjectId]);

  


  
  // Auto-switch to sprint tab when a sprint is selected, and vice-versa
  useEffect(() => {
    if (selectedSprintId && activeTab !== 'sprint') {
      setActiveTab('sprint');
    }
  }, [selectedSprintId]);

  // Pre-select the current active sprint or the first planned one on load
  useEffect(() => {
    if (sprints.length > 0 && !selectedSprintId && activeProjectId) {
      const active = sprints.find(s => s.status === 'active');
      if (active) {
        setSelectedSprintId(active._id);
      } else {
        const firstPlanned = sprints.find(s => s.status === 'planned');
        if (firstPlanned) setSelectedSprintId(firstPlanned._id);
      }
    }
  }, [sprints, activeProjectId]);

  // Calculate first available date for new sprints
  useEffect(() => {
    if (sprints.length > 0 && isCreateModalOpen && !newSprint.name) {
      const activeSprints = sprints.filter(s => s.status !== 'closed' && s.status !== 'completed');
      if (activeSprints.length > 0) {
        const maxEndDate = activeSprints.reduce((max, s) => {
          const end = new Date(s.endDate);
          return end > max ? end : max;
        }, new Date(0));
        
        // Use the end date of the last sprint as the start of the next (per user request example)
        const nextStart = maxEndDate;
        const formattedDate = nextStart.toISOString().split('T')[0];
        
        if (newSprint.startDate !== formattedDate) {
          setNewSprint(prev => ({ ...prev, startDate: formattedDate }));
        }
      }
    }
  }, [sprints, isCreateModalOpen]);

  const selectedSprint = sprints?.find(s => s._id === selectedSprintId);

  const capacityData = React.useMemo(() => {
    if (!selectedSprint) return null;
    return {
      capacity: selectedSprint.capacity || getSprintCapacity(selectedSprint, POINTS_PER_DAY),
      targetCapacity: selectedSprint.targetCapacity || ((selectedSprint.capacity || getSprintCapacity(selectedSprint, POINTS_PER_DAY)) * 0.8),
      metrics: selectedSprint.metrics || {}
    };
  }, [selectedSprint, activeProject, POINTS_PER_DAY]); // activeProject affects POINTS_PER_DAY



  const getBacklogInsight = () => {
    const backlogCount = stats?.summary?.backlogCount || 0;
    if (!stats?.insights || backlogCount === 0) return t('backlogHealthy');
    
    // If backlog is tiny, don't show growth/stability alerts, just say it's healthy
    if (backlogCount < 5) return t('backlogHealthy');
    
    const creationRateRaw = parseFloat(stats.insights.creationRate || 0);
    const resolutionRateRaw = parseFloat(stats.insights.resolutionRate || 0);
    const creationRate = Math.round(creationRateRaw);
    const resolutionRate = Math.round(resolutionRateRaw);
    const { isGrowing, isStable } = stats.insights;
    
    // Prioritize Stable if it's statistically stable
    if (isStable) {
       return t('backlogStable')
        .replace('{{rate}}', creationRate);
    }
    
    if (isGrowing) {
      return (creationRateRaw > resolutionRateRaw * 1.5 ? t('backlogGrowingWarning') || t('backlogGrowing') : t('backlogGrowing'))
        .replace('{{created}}', creationRate)
        .replace('{{resolved}}', resolutionRate);
    }
    
    return t('backlogImproving')
      .replace('{{resolved}}', resolutionRate)
      .replace('{{created}}', creationRate);
  };

  // Pre-normalize search query once to avoid redundant work in filter loops
  const normalizedSearch = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const cleanQ = q.replace(/^vg-/, '').replace(/^#/, '');
    return { q, cleanQ };
  }, [searchQuery]);

  const filteredBacklog = useMemo(() => {
    // Note: Most filtering is now handled server-side via SWR key.
    // Client-side filtering is kept as a fallback for local search/mutations if needed,
    // but the source of truth is the paginated data.
    return allTickets;
  }, [allTickets]);

  // Hybrid Search Logic: Separate effect for server-side search to avoid "flashing" or re-renders during memoization
  useEffect(() => {
    if (searchQuery.length > 2 && filteredBacklog.length === 0 && searchQuery !== serverSearchTerm) {
      const timer = setTimeout(() => {
        setServerSearchTerm(searchQuery);
      }, 500); // 500ms debounce
      return () => clearTimeout(timer);
    } else if (searchQuery === "" && serverSearchTerm !== "") {
      setServerSearchTerm("");
    }
  }, [searchQuery, filteredBacklog.length, serverSearchTerm]);

  const backlogTickets = filteredBacklog;
  
  // Consolidated Sprint Ticket Logic with Search Support
  const sprintTickets = selectedSprintTickets;
  const sortedSprintTickets = useMemo(() => {
    return getSorted(selectedSprintTickets);
  }, [selectedSprintTickets, getSorted]);

  // Virtualizer setup for Backlog
  const rowVirtualizer = useVirtualizer({
    count: filteredBacklog.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 92, // Static estimate to eliminate forced reflows
    overscan: 5
  });

  // Virtualizer setup for Sprint Items
  const sprintVirtualizer = useVirtualizer({
    count: sortedSprintTickets.length,
    getScrollElement: () => sprintListRef.current,
    estimateSize: () => 92,
    overscan: 5
  });

  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const lastPage = infiniteData?.[infiniteData.length - 1];
        const totalItems = lastPage?.pagination?.total || 0;
        const totalPages = Math.ceil(totalItems / 10);
        const hasMore = totalItems > allTickets.length && size < totalPages;

        if (entries[0].isIntersecting && !isLoadingTickets && !swrValidating && hasMore) {
          setSize(s => s + 1);
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [infiniteData, isLoadingTickets, swrValidating, setSize, allTickets.length, size]);

  useEffect(() => {
    if (!sprintLoadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const lastPage = sprintInfiniteData?.[sprintInfiniteData.length - 1];
        const totalItems = lastPage?.pagination?.total || 0;
        const totalPages = Math.ceil(totalItems / 10);
        const hasMore = totalItems > selectedSprintTickets.length && sprintSize < totalPages;

        if (entries[0].isIntersecting && !sprintSwrValidating && hasMore) {
          setSprintSize(s => s + 1);
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    observer.observe(sprintLoadMoreRef.current);
    return () => observer.disconnect();
  }, [sprintInfiniteData, sprintSwrValidating, setSprintSize, selectedSprintTickets.length, sprintSize]);

  const currentSprintScore = React.useMemo(() => selectedSprintTickets.reduce((acc, t) => acc + (t.planning?.score || 0), 0), [selectedSprintTickets]);

  const getSprintPoints = (sId) => {
    if (sId === selectedSprintId) return currentSprintScore;
    // Fallback for other sprints in the list summary if they happen to be in the backlog cache (unlikely)
    return (allTickets || [])
      .filter(t => String(t.sprintId?._id || t.sprintId) === String(sId))
      .reduce((acc, t) => acc + (t.planning?.score || 0), 0);
  };


  const handleCreateSprint = async (e) => {
    e.preventDefault();
    const startDate = new Date(newSprint.startDate);
    const duration = parseInt(newSprint.duration);
    
    if (isNaN(startDate.getTime()) || isNaN(duration) || duration <= 0) {
      addToast(t('toastError'), 'error');
      return;
    }

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + duration);

    const hasOverlap = Array.isArray(sprints) && sprints
      .filter(s => s.status === 'active' || s.status === 'planned')
      .some(s => {
        const sStart = new Date(s.startDate);
        const sEnd = new Date(s.endDate);
        if (isNaN(sStart.getTime()) || isNaN(sEnd.getTime())) return false;
        return (startDate < sEnd && endDate > sStart);
      });

    if (hasOverlap) {
      addToast(t('toastSprintOverlapError'), 'error');
      return;
    }

    setIsCreating(true);
    try {
      const res = await api.post('/sprints', { 
        projectId: activeProjectId, 
        name: newSprint.name, 
        startDate, 
        endDate, 
        status: 'draft' // New system starts as draft
      });
      await mutateSprints();
      setSelectedSprintId(res.data._id);
      setIsCreateModalOpen(false);
      setNewSprint({ name: '', duration: '14', startDate: new Date().toISOString().split('T')[0] });
      addToast(`${t('sprintCreated')}: ${res.data.name}`, 'success');
    } catch (err) { /* Silent fail */ } finally { setIsCreating(false); }
  };

  const handleInitiatePlanning = async () => {
    if (!selectedSprintId) return;
    setIsLocking(true);
    try {
      // 1. Calculate Capacity
      const capRes = await api.post(`/projects/${activeProjectId}/calculate-capacity`, {
        baseScorePerPerson: activeProject?.settings?.pointsPerDay || 25
      });
      
      const { capacity, targetCapacity, metrics } = capRes.data;

      // 2. Update Sprint with locked values
      await api.patch(`/sprints/${selectedSprintId}`, {
        capacity,
        targetCapacity,
        status: 'planned' // Transition to build phase
      });

      await mutateSprints();
      addToast(t('capacityCalculated'), 'success');
    } catch (err) {
      addToast(t('toastError'), 'error');
    } finally {
      setIsLocking(false);
    }
  };

  const handleSuggestNext = async () => {
    if (!selectedSprintId) return;
    setIsSuggesting(true);
    try {
      const res = await api.get(`/sprints/${selectedSprintId}/suggest-next`);
      if (res.data.suggestion) {
        setSelectedTicket(res.data.suggestion);
        addToast(t('suggestionFound'), 'success');
      } else {
        addToast(t('noCompatibleTickets'), 'info');
      }
    } catch (err) {
      /* Silent fail */
    } finally {
      setIsSuggesting(false);
    }
  };

  const addToSprint = async (ticketId) => {
    if (!selectedSprintId) return;
    const ticket = allTickets.find(t => t._id === ticketId);
    const isPlanned = (ticket?.status === 'bug_fixing' || ticket?.type === 'bug' || ticket?.originalStatus === 'bug_fixing') || (ticket?.planning && typeof ticket?.planning.score === 'number');
    
    if (!isPlanned) {
      addToast(t('planningRequiredToSprint'), 'warning');
      return;
    }

    try {
      // Optimistic local update
      if (allTickets) {
        const updatedTickets = allTickets.map(t => 
          t._id === ticketId ? { 
            ...t, 
            sprintId: selectedSprintId, 
            status: (t.status === 'bug_fixing' || t.status === 'ready_for_review' || t.type === 'bug' || t.originalStatus === 'bug_fixing') 
              ? (t.status === 'ready_for_review' ? 'ready_for_review' : 'bug_fixing') 
              : 'todo' 
          } : t
        );
        // Note: This logic assumes a flat structure for simplicity in this example
        mutateTickets(infiniteData.map(page => ({ ...page, data: updatedTickets.filter(t => !t.sprintId || t.sprintId === 'backlog') })), false);
      }
      
      await api.post(`/sprints/${selectedSprintId}/tickets`, { ticketIds: [ticketId] });
      await mutateTickets();
      await mutateSelectedSprint();
      addToast(t('toastTicketAdded'), 'success');
    } catch (err) { 
      addToast(t('toastError'), 'error');
    }
  };

  const removeFromSprint = (ticketId) => {
    setConfModal({
      isOpen: true,
      type: 'danger',
      title: t('confirmRemoveFromSprint'),
      message: t('confirmRemoveTicketMsg'),
      onConfirm: async () => {
        try {
          // Optimistic local update
          if (allTickets) {
            const updatedTickets = allTickets.map(t => 
              t._id === ticketId ? { 
                ...t, 
                sprintId: null, 
                status: (t.status === 'bug_fixing' || t.status === 'ready_for_review' || t.type === 'bug' || t.originalStatus === 'bug_fixing') 
                  ? (t.status === 'ready_for_review' ? 'ready_for_review' : 'bug_fixing') 
                  : 'backlog' 
              } : t
            );
            mutateTickets(infiniteData.map(page => ({ ...page, data: updatedTickets })), false);
          }
          
          await api.delete(`/sprints/${selectedSprintId}/tickets`, { data: { ticketIds: [ticketId] } });
          await mutateTickets();
          await mutateSelectedSprint();
          addToast(t('toastTicketRemoved'), 'success');
        } catch (err) { 
          addToast(t('toastError'), 'error');
        }
      }
    });
  };

  const handleDeleteSprint = (sprintId, e) => {
    e.stopPropagation();
    const sprint = sprints?.find(s => s._id === sprintId);
    
    setConfModal({
      isOpen: true,
      type: 'danger',
      title: t('confirmDeleteSprint'),
      message: t('confirmDeleteSprintMsg'),
      expectedName: sprint?.name,
      onConfirm: async () => {
        try {
          // Optimistic local update
          if (allTickets) {
            const updatedTickets = allTickets.map(t => 
              t.sprintId === sprintId ? { 
                ...t, 
                sprintId: null, 
                status: (t.status === 'bug_fixing' || t.status === 'ready_for_review' || t.type === 'bug' || t.originalStatus === 'bug_fixing') 
                  ? (t.status === 'ready_for_review' ? 'ready_for_review' : 'bug_fixing') 
                  : 'backlog' 
              } : t
            );
            mutateTickets(infiniteData.map(page => ({ ...page, data: updatedTickets })), false);
          }
          await api.delete(`/sprints/${sprintId}`);
          if (selectedSprintId === sprintId) setSelectedSprintId(null);
          await mutateSprints();
          await mutateTickets();
          addToast(t('toastSprintDeleted'), 'success');
        } catch (err) { 
          addToast(t('toastError'), 'error');
        }
      }
    });
  };

  const handleDeleteTicket = (ticketId, ticketTitle, ticketKey, e) => {
    e.stopPropagation();
    
    setConfModal({
      isOpen: true,
      type: 'danger',
      title: t('confirmDeleteTicket'),
      message: t('confirmDeleteTicketMsg', { id: ticketKey }),
      onConfirm: async () => {
        try {
          await api.delete(`/tickets/${ticketId}`);
          if (allTickets) {
            const updated = allTickets.filter(t => t._id !== ticketId);
            mutateTickets(infiniteData.map(page => ({ ...page, data: updated })), false);
          }
          addToast(t('toastTicketDeleted'), 'success');
        } catch (err) {
          addToast(t('toastError'), 'error');
        }
      }
    });
  };

  const handleCloseSprint = (sprintId, e) => {
    e.stopPropagation();
    const sprint = sprints?.find(s => s._id === sprintId);
    const nextSprint = sprints?.find(s => s.status === 'planned');
    
    setConfModal({
      isOpen: true,
      type: 'primary',
      title: t('confirmCloseSprint'),
      message: t('confirmCloseSprintMsg'),
      confirmText: t('closeSprint'),
      onConfirm: async () => {
        try {
          // Fetch all tickets for this sprint, including those already marked as deleted
          const res = await api.get(`/tickets?projectId=${activeProjectId}&sprintId=${sprintId}&includeDeleted=true`);
          const sprintTickets = res.data.data || [];
          
          const incompleteTicketIds = sprintTickets.filter(t => !t.isDeleted && t.status !== 'done' && t.status !== 'completed').map(t => t._id);
          
          const snapshot = {
            initialPlanned: sprint?.initialPlanned || sprintTickets.length,
            total: sprintTickets.length,
            completed: sprintTickets.filter(t => !t.isDeleted && t.status === 'done').length,
            moved: incompleteTicketIds.length,
            movedToNext: !!nextSprint,
            tickets: sprintTickets.map(t => ({
              _id: t._id,
              key: t.key,
              title: t.title || t.message,
              status: t.status,
              priority: t.priority,
              createdAt: t.createdAt,
              isDeleted: t.isDeleted,
              deletedAt: t.deletedAt
            }))
          };
            
          if (incompleteTicketIds.length > 0) {
            if (nextSprint) {
              await api.post(`/sprints/${nextSprint._id}/tickets`, { ticketIds: incompleteTicketIds });
            } else {
              await api.delete(`/sprints/${sprintId}/tickets`, { data: { ticketIds: incompleteTicketIds } });
            }
          }
          
          await api.patch(`/sprints/${sprintId}`, { 
            status: 'closed',
            description: JSON.stringify(snapshot)
          });
          
          if (nextSprint && new Date(nextSprint.startDate) <= new Date()) {
            await api.patch(`/sprints/${nextSprint._id}`, { status: 'active' });
          }
          
          await mutateSprints();
          await mutateTickets();
          addToast(t('toastSprintClosed'));
        } catch (err) {
          addToast(t('toastError'), 'error');
        }
      }
    });
  };


const CapacityMeterSkeleton = () => (
  <div className="p-6 rounded-[28px] bg-white/[0.02] border border-white/5 space-y-6 shadow-sm backdrop-blur-sm h-full relative overflow-hidden">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Skeleton width="16px" height="16px" borderRadius="50%" />
        <Skeleton width="100px" height="10px" />
      </div>
      <Skeleton width="50px" height="20px" borderRadius="0.5rem" />
    </div>
    <div className="space-y-4">
      <div className="flex justify-between items-baseline">
        <Skeleton width="80px" height="24px" />
        <Skeleton width="60px" height="12px" />
      </div>
      <Skeleton width="100%" height="10px" borderRadius="1rem" />
      <div className="flex justify-between">
        <Skeleton width="140px" height="8px" />
        <Skeleton width="100px" height="8px" />
      </div>
    </div>
  </div>
);

const TicketSkeleton = React.memo(() => (
  <div className="w-full mb-3">
    <div className="pl-7 pr-4 py-4 rounded-[20px] bg-white/[0.02] border border-white/5 flex items-center justify-between">
      <div className="flex items-center gap-4 flex-1">
        <Skeleton width="40px" height="40px" borderRadius="0.75rem" />
        <div className="flex flex-col gap-2 flex-1">
          <div className="flex items-center gap-3">
            <Skeleton width="16%" height="16px" borderRadius="0.375rem" />
            <Skeleton width="33%" height="16px" borderRadius="0.375rem" />
          </div>
          <div className="flex items-center gap-4">
            <Skeleton width="80px" height="8px" />
            <Skeleton width="96px" height="8px" />
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Skeleton width="32px" height="32px" borderRadius="0.75rem" />
        <Skeleton width="32px" height="32px" borderRadius="0.75rem" />
      </div>
    </div>
  </div>
));

const BacklogPageSkeleton = ({ t }) => (
  <div className="flex flex-col h-[calc(100vh-64px)] overflow-hidden p-8 lg:p-10">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 flex-1 min-h-0">
      <div className="lg:col-span-4 space-y-6 flex flex-col min-h-0">
        <Skeleton width="80px" height="12px" className="px-2" />
        <div className="flex-1 overflow-y-auto space-y-3 pr-3 pl-3">
          {[1, 2, 3, 4].map(i => <SprintSkeleton key={i} />)}
        </div>
      </div>
      <div className="lg:col-span-8 flex flex-col min-h-0 space-y-6">
        <div className="flex-1 bg-white/[0.01] border border-white/5 rounded-[32px] p-6 flex flex-col min-h-0">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 mt-2">
            <div className="flex p-1.5 bg-black/20 border border-white/5 rounded-[22px] w-fit relative">
               <Skeleton width="120px" height="40px" borderRadius="18px" />
               <Skeleton width="120px" height="40px" borderRadius="18px" className="ml-2" />
            </div>
            <Skeleton width="120px" height="40px" borderRadius="12px" />
          </div>
          <div className="flex-1 overflow-y-auto space-y-3">
            {[1, 2, 3, 4, 5].map(i => <TicketSkeleton key={i} />)}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const CapacityMeter = React.memo(({ currentScore, capacity, targetCapacity, metrics, t }) => {
  if (!capacity) return <CapacityMeterSkeleton />;

  const percentage = Math.min(100, (currentScore / capacity) * 100);
  const targetPoints = targetCapacity || (capacity * 0.8);
  const targetPercentage = (targetPoints / capacity) * 100;
  
  let status = 'safe';
  let statusColor = '#3b82f6'; 
  if (currentScore > capacity) { 
    status = 'overflow'; 
    statusColor = '#f43f5e'; 
  }
  else if (currentScore > targetPoints) {
    status = 'warning';
    statusColor = '#f59e0b'; 
  }

  return (
    <div className="p-6 rounded-[28px] bg-white/[0.02] border border-white/5 space-y-4 shadow-sm backdrop-blur-sm relative overflow-hidden group/meter h-full">
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-2">
          <Zap size={16} style={{ color: statusColor }} className={status === 'overflow' ? 'animate-pulse' : ''} />
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('capacityMeter')}</span>
        </div>
        <div className="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border transition-all duration-500" style={{ backgroundColor: `${statusColor}15`, color: statusColor, borderColor: `${statusColor}20` }}>
          {t(status)}
        </div>
      </div>

      <div className="space-y-3 relative z-10">
        <div className="flex justify-between text-[11px] font-black text-slate-300 uppercase tracking-tight">
          <div className="flex items-baseline gap-1">
            <span className="text-lg leading-none" style={{ color: statusColor }}>{currentScore}</span>
            <span className="text-[9px] text-slate-500">{t('points')}</span>
          </div>
          <div className="flex items-baseline gap-1 opacity-60">
            <span className="text-sm leading-none">{capacity}</span>
            <span className="text-[9px] text-slate-500">{t('totalCapacity')}</span>
          </div>
        </div>
        <div className="h-2.5 bg-white/5 rounded-full overflow-hidden relative">
          <div className="absolute top-0 bottom-0 border-r border-white/20 z-10 w-0" style={{ left: `${targetPercentage}%` }} />
          <motion.div 
            initial={false}
            animate={{ width: `${percentage}%` }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
            className="h-full rounded-full"
            style={{ backgroundColor: statusColor, boxShadow: `0 0 20px ${statusColor}40` }}
          />
        </div>
        <div className="flex justify-between items-center">
          <p className="text-[9px] text-slate-500 font-medium italic opacity-40">{t('commitmentCapacity')}: {Math.round(targetPoints)} {t('points')}</p>
          {metrics && (
            <div className="flex gap-3">
              <div className="flex items-center gap-1">
                <span className="text-[8px] font-black text-slate-600 uppercase">{t('uncertaintyFactor')}:</span>
                <span className="text-[9px] font-bold text-slate-400">{metrics.uncertaintyFactor}x</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.01] to-transparent pointer-events-none" />
    </div>
  );
});


const TicketRow = React.memo(({ 
  ticket, 
  onClick, 
  onDelete, 
  onAction, 
  actionIcon: ActionIcon,
  isSelected, 
  isRestricted, 
  t, 
  lang, 
  virtualRow, 
  measureElement 
}) => {
  if (!ticket) return null;
  const ps = getPriorityStyles(ticket.priority || 'medium');
  const isPlanned = (ticket.status === 'bug_fixing' || ticket.type === 'bug' || ticket.originalStatus === 'bug_fixing') || (ticket.planning && typeof ticket.planning.score === 'number');

  return (
    <div
      key={ticket._id}
      data-index={virtualRow.index}
      ref={measureElement}
      className="absolute top-0 left-0 w-full"
      style={{
        transform: `translateY(${virtualRow.start}px)`,
        paddingBottom: '12px'
      }}
    >
      <div 
        onClick={() => onClick(ticket)} 
        className={`pl-7 pr-4 py-4 rounded-[20px] border flex items-center justify-between group transition-all cursor-pointer ${isSelected ? 'ring-2 ring-primary ring-inset shadow-xl shadow-primary/10' : 'hover:border-white/20'}`} 
        style={{ backgroundColor: ps.bgSubtle, borderColor: isSelected ? 'rgba(59, 130, 246, 0.5)' : ps.borderSubtle }}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
           <div className="p-2.5 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: ps.bgSubtle, color: ps.text }}>
             {(ticket.status === 'bug_fixing' || ticket.type === 'bug' || ticket.originalStatus === 'bug_fixing') ? <Bug size={18} /> : <CheckCircle2 size={18} />}
           </div>
           <div className="flex flex-col gap-1 min-w-0">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 shrink-0">
                  <CopyableBadge 
                    text={ticket.key || `#${ticket._id.slice(-6).toUpperCase()}`} 
                    styles={{ bg: ps.bg, text: ps.text, border: ps.border }} 
                  />
                  {ticket.planning?.score && (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-500 text-[10px] font-black border border-amber-500/20 shadow-sm shadow-amber-500/5">
                      <Zap size={10} fill="currentColor" />
                      {ticket.planning.score}
                    </div>
                  )}
                </div>
                <h4 className="font-bold text-slate-100 text-sm line-clamp-1">{ticket.title || ticket.message}</h4>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-[10px] uppercase font-black tracking-widest flex items-center gap-2" style={{ color: ps.text }}>
                  {t('priority')}: {t(ticket.priority || 'medium')}
                </p>
                <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider opacity-40">
                  <Clock size={10} />
                  {new Date(ticket.createdAt).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
           </div>
        </div>
        <div className="flex gap-4 items-center">
          {!isPlanned && (
            <div className="px-3 py-1.5 rounded-xl bg-amber-500/10 text-amber-500 text-[9px] font-black border border-amber-500/20 shadow-lg shadow-amber-500/5 animate-pulse flex items-center gap-2">
              <Zap size={10} fill="currentColor" />
              {t('pendingPlanning')}
            </div>
          )}
          {onAction && (
             <button 
              onClick={(e) => { e.stopPropagation(); onAction(ticket._id); }} 
              className="p-2 rounded-xl transition-all hover:bg-white/10" 
              style={{ color: ps.text }}
            >
              {ActionIcon && <ActionIcon size={16} />}
            </button>
          )}
          {onDelete && !isRestricted && (
            <button 
              onClick={(e) => onDelete(ticket._id, ticket.title || ticket.message, ticket.key || `#${ticket._id.slice(-6).toUpperCase()}`, e)} 
              className="p-2 rounded-xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all shadow-lg shadow-rose-500/10"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

  if (isInitialLoading) return <BacklogPageSkeleton t={t} />;

  if (filteredProjects.length === 0 && isInitialized && !isLoadingProjects && !activeProjectId) {
    return (
      <EmptyState 
        icon={Database}
        title={t('noProjectsInWorkspace')}
        description={t('noProjectsInWorkspaceDesc')}
        actionLabel={t('goProjects')}
        onAction={() => navigate('/projects')}
        className="h-[calc(100vh-160px)]"
      />
    );
  }

  if (!activeProjectId && filteredProjects.length > 0) {
    return (
      <EmptyState 
        icon={Zap}
        title={t('selectProjectTitle')}
        description={t('selectProjectDesc')}
        className="h-[calc(100vh-160px)]"
      />
    );
  }

  const sortedSprints = [...(sprints || [])]
    .filter(s => s.status !== 'closed')
    .sort((a, b) => {
      if (a.status === 'active') return -1;
      if (b.status === 'active') return 1;
      return new Date(a.startDate) - new Date(b.startDate);
    });

  return (
    <>
    <div className="flex flex-col h-[calc(100vh-64px)] overflow-hidden p-8 lg:p-10">

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 flex-1 min-h-0">
        <div className="lg:col-span-4 space-y-6 flex flex-col min-h-0">
          <h2 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 px-2">{t('sprints')}</h2>
          <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-3 pl-3">
            {sortedSprints.length === 0 ? <p className="text-slate-500 text-center py-8">{t('noSprints')}</p> : sortedSprints.map(sprint => (
              <div 
                key={sprint._id} 
                onClick={() => setSelectedSprintId(sprint._id)} 
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedSprintId(sprint._id); }}
                className={`w-full text-left p-4 rounded-2xl border transition-all relative group/sprint cursor-pointer 
                  ${selectedSprintId === sprint._id 
                    ? `${sprint.status === 'active' ? 'border-emerald-500 shadow-lg shadow-emerald-500/10' : 'border-primary shadow-lg shadow-primary/10'}` 
                    : sprint.status === 'active' 
                      ? 'bg-emerald-500/5 border-emerald-500/20 hover:bg-emerald-500/10 hover:border-emerald-500/30' 
                      : 'bg-white/[0.02] border-white/5 hover:border-white/10'
                  }`}
              >
                {selectedSprintId === sprint._id && (
                  <motion.div 
                    layoutId="active-sprint-bg"
                    className={`absolute inset-0 rounded-2xl -z-10 ${sprint.status === 'active' ? 'bg-emerald-500/10' : 'bg-primary/10'}`}
                    transition={springTransition}
                  />
                )}
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-white truncate pr-2">{sprint.name}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${sprint.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}`}>{t(sprint.status)}</span>
                    {sprint.status === 'active' ? (
                      <button onClick={(e) => handleCloseSprint(sprint._id, e)} className="opacity-0 group-hover/sprint:opacity-100 p-1.5 rounded-lg bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500 hover:text-white transition-all"><CheckCircle2 size={13} /></button>
                    ) : (
                      <button onClick={(e) => handleDeleteSprint(sprint._id, e)} className="opacity-0 group-hover/sprint:opacity-100 p-1.5 rounded-lg bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all"><Trash2 size={13} /></button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                  <Calendar size={12} />
                  {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
                  <div className="ml-auto flex items-center gap-1.5">
                    {(() => {
                      const points = getSprintPoints(sprint._id);
                      const capacity = getSprintCapacity(sprint, POINTS_PER_DAY);
                      const isOver = points > capacity;
                      return (
                        <div className={`flex items-center gap-1 font-black uppercase text-[8px] transition-colors ${isOver ? 'text-rose-500 animate-pulse' : 'text-primary'}`}>
                          <Zap size={10} />
                          {t('pointLoad')}: {points} / {capacity}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-8 flex flex-col min-h-0 space-y-6">
          <div className="flex-1 bg-white/[0.01] border border-white/5 rounded-[32px] p-6 flex flex-col min-h-0">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 mt-2">
                <div className="flex p-1.5 bg-black/20 border border-white/5 rounded-[22px] w-fit relative">
                  <button 
                    onClick={() => setActiveTab('sprint')}
                    className={`relative px-6 py-2.5 rounded-[18px] text-[11px] font-black uppercase tracking-widest transition-all duration-300 z-10 flex items-center gap-2 ${activeTab === 'sprint' ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}
                  >
                    {activeTab === 'sprint' && (
                      <motion.div 
                        layoutId="activeTab"
                        className="absolute inset-0 bg-primary rounded-[18px] -z-10 shadow-lg shadow-primary/20"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <Zap size={14} className={activeTab === 'sprint' ? 'text-white' : 'text-slate-500'} />
                    {t('sprintItems')}
                    {activeTab === 'sprint' ? (
                      <span className={`ml-1 text-[9px] px-1.5 py-0.5 rounded-md ${activeTab === 'sprint' ? 'bg-white/20 text-white' : 'bg-white/5 text-slate-500'}`}>
                        {dashboardCounts?.sprint ?? (sprintInfiniteData?.[0]?.pagination?.total || 0)}
                      </span>
                    ) : (
                      <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-md bg-white/5 text-slate-500">
                         {dashboardCounts?.sprint ?? (sprintInfiniteData?.[0]?.pagination?.total || 0)}
                      </span>
                    )}
                  </button>
                  <button 
                    onClick={() => setActiveTab('backlog')}
                    className={`relative px-6 py-2.5 rounded-[18px] text-[11px] font-black uppercase tracking-widest transition-all duration-300 z-10 flex items-center gap-2 ${activeTab === 'backlog' ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}
                  >
                    {activeTab === 'backlog' && (
                      <motion.div 
                        layoutId="activeTab"
                        className="absolute inset-0 bg-primary rounded-[18px] -z-10 shadow-lg shadow-primary/20"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <Layers size={14} className={activeTab === 'backlog' ? 'text-white' : 'text-slate-500'} />
                    {t('backlog')}
                    <span className={`ml-1 text-[9px] px-1.5 py-0.5 rounded-md ${activeTab === 'backlog' ? 'bg-white/20 text-white' : 'bg-white/5 text-slate-500'}`}>
                      {dashboardCounts?.backlog ?? (infiniteData?.[0]?.pagination?.total || 0)}
                    </span>
                  </button>
                </div>
                
                <div className="flex items-center gap-3">
                  {selectedSprintId && activeTab === 'sprint' && (
                    <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/5 border border-primary/10 mr-2">
                       <Zap size={14} className="text-primary" />
                       <span className="text-[10px] font-black uppercase tracking-widest text-primary/80">
                         {currentSprintScore} {t('points')}
                       </span>
                    </div>
                  )}
                  {(hasJira || hasAzure) && (
                    <button 
                      onClick={() => setShowSyncModal(true)} 
                      className="flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 text-slate-300 rounded-xl font-bold hover:bg-white/10 transition-all text-xs group"
                    >
                      <RefreshCw size={14} className="group-hover:rotate-180 transition-all duration-500" />
                      {t('syncExternal')}
                    </button>
                  )}
                  <button 
                    onClick={() => setIsCreateModalOpen(true)} 
                    className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl font-bold hover:bg-blue-600 transition-all shadow-lg text-xs"
                  >
                    <Plus size={16} />
                    {t('createSprint')}
                  </button>
                </div>
              </div>


            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="flex items-center gap-3">
                  <div className="relative group/search flex-1">
                    <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within/search:text-primary transition-colors" />
                    <input 
                      type="text" 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('searchTickets')} 
                      className="w-full bg-black/40 border border-white/10 rounded-[20px] pl-14 pr-4 py-4 text-sm text-slate-100 focus:border-primary/50 outline-none transition-all focus:bg-black/60 group-hover/search:border-white/20 shadow-inner" 
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  {activeTab === 'backlog' ? (
                    <button 
                      onClick={() => setIsFilterModalOpen(true)}
                      className={`p-4 rounded-[20px] border transition-all duration-300 flex items-center gap-2 ${
                        (backlogFilters.priority !== 'all' || backlogFilters.planning !== 'all')
                          ? 'bg-primary/10 border-primary/30 text-primary' 
                          : 'bg-black/40 border-white/10 text-slate-400 hover:bg-black/60 hover:border-white/20'
                      }`}
                    >
                      <Filter size={18} />
                      {(backlogFilters.priority !== 'all' || backlogFilters.planning !== 'all') && (
                        <span className="text-[10px] font-black uppercase tracking-widest hidden md:block">{t('filtered')}</span>
                      )}
                    </button>
                  ) : (
                    <button 
                      onClick={() => setIsFilterModalOpen(true)}
                      className={`p-4 rounded-[20px] border transition-all duration-300 flex items-center gap-2 ${
                        (sprintFilters.priority !== 'all' || sprintFilters.status !== 'all')
                          ? 'bg-primary/10 border-primary/30 text-primary' 
                          : 'bg-black/40 border-white/10 text-slate-400 hover:bg-black/60 hover:border-white/20'
                      }`}
                    >
                      <Filter size={18} />
                      {(sprintFilters.priority !== 'all' || sprintFilters.status !== 'all') && (
                        <span className="text-[10px] font-black uppercase tracking-widest hidden md:block">{t('filtered')}</span>
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 relative overflow-hidden">
                <AnimatePresence initial={false}>
                  {/* Sprint View Layer */}
                  <motion.div 
                    key="sprint-view"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ 
                      opacity: activeTab === 'sprint' ? 1 : 0,
                      x: activeTab === 'sprint' ? 0 : 20,
                      pointerEvents: activeTab === 'sprint' ? 'auto' : 'none',
                      visibility: activeTab === 'sprint' ? 'visible' : 'hidden'
                    }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    className="absolute inset-0 flex flex-col min-h-0"
                  >
                    {selectedSprintId && selectedSprint && (
                      <div className="mb-6 space-y-4">
                        {selectedSprint.status === 'draft' ? (
                          <div className="p-8 rounded-[32px] bg-primary/5 border border-dashed border-primary/20 flex flex-col items-center justify-center text-center animate-in zoom-in-95 duration-500">
                            <div className="w-16 h-16 bg-primary/10 rounded-3xl flex items-center justify-center text-primary mb-4">
                              <Zap size={32} />
                            </div>
                            <h3 className="text-xl font-black text-white mb-2">{t('initiatePlanning')}</h3>
                            <p className="text-slate-500 text-sm max-w-xs mb-6">
                              {t('lockPlanningDescWithPoints', { 
                                points: getSprintCapacity(selectedSprint, POINTS_PER_DAY),
                                days: getSprintDays(selectedSprint)
                              })}
                            </p>
                            <button 
                              onClick={handleInitiatePlanning}
                              disabled={isLocking}
                              className="px-8 py-4 bg-primary text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-blue-600 transition-all shadow-xl shadow-primary/20 disabled:opacity-50"
                            >
                              {isLocking ? t('loading') : t('lockCapacity')}
                            </button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
                            <CapacityMeter 
                              currentScore={currentSprintScore} 
                              capacity={capacityData?.capacity || getSprintCapacity(selectedSprint, POINTS_PER_DAY)} 
                              targetCapacity={capacityData?.targetCapacity}
                              metrics={capacityData?.metrics}
                              t={t} 
                            />
                            {selectedSprint.status === 'planned' && (
                              <div className="p-6 rounded-[28px] bg-white/[0.01] border border-white/5 flex flex-col gap-4 h-full">
                                 <div className="flex items-center gap-2">
                                   <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                                     <Plus size={16} />
                                   </div>
                                   <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('suggestNext')}</span>
                                 </div>
                                 <p className="text-xs text-slate-400 leading-relaxed">{t('suggestNextDesc')}</p>
                                 <button 
                                  onClick={handleSuggestNext}
                                  disabled={isSuggesting}
                                  className="mt-auto w-full py-3 bg-primary/10 hover:bg-primary/20 border border-primary/20 hover:border-primary/40 rounded-2xl text-[10px] font-black uppercase tracking-widest text-primary hover:text-white transition-all shadow-xl shadow-primary/5 hover:shadow-primary/20 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50"
                                 >
                                   {isSuggesting ? t('loading') : t('suggestNext')}
                                 </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {selectedSprintId ? (
                      <div className="flex-1 flex flex-col min-h-0 relative">
                        <div 
                          ref={sprintListRef}
                          className="flex-1 overflow-y-auto custom-scrollbar pr-3 relative"
                        >
                          <div
                            style={{
                              height: `${sprintVirtualizer.getTotalSize()}px`,
                              width: '100%',
                              position: 'relative',
                            }}
                          >
                            {(isLoadingSprint || (sprintSwrValidating && !sortedSprintTickets.length)) ? (
                              Array.from({ length: 5 }).map((_, i) => (
                                <div key={`skeleton-sprint-${i}`} style={{ transform: `translateY(${i * 84}px)`, position: 'absolute', width: '100%' }}>
                                   <TicketSkeleton />
                                </div>
                              ))
                            ) : sortedSprintTickets.length === 0 && !sprintSwrValidating ? (
                              <div className="flex-1 flex flex-col items-center justify-center p-12 text-center animate-in fade-in zoom-in-95 duration-700 min-h-[400px]">
                                <div className="relative mb-8">
                                  <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full opacity-50" />
                                  <div className="relative w-28 h-28 bg-white/[0.02] border border-white/5 rounded-[40px] flex items-center justify-center transform hover:rotate-6 transition-all duration-700">
                                    <Zap size={48} className="text-slate-700" strokeWidth={1} />
                                  </div>
                                </div>
                                <h3 className="text-xl font-black text-white mb-3 italic tracking-tight uppercase">
                                  {serverSearchTerm || sprintFilters.status !== 'all' ? t('noResultsTitle') : t('emptySprintTitle')}
                                </h3>
                              </div>
                            ) : sprintVirtualizer.getVirtualItems().map((virtualRow) => (
                              <TicketRow
                                key={sortedSprintTickets[virtualRow.index]?._id || virtualRow.key}
                                ticket={sortedSprintTickets[virtualRow.index]}
                                onClick={setSelectedTicket}
                                onDelete={handleDeleteTicket}
                                onAction={removeFromSprint}
                                actionIcon={X}
                                isRestricted={selectedSprint?.status === 'closed'}
                                t={t}
                                lang={lang}
                                virtualRow={virtualRow}
                              />
                            ))}
                          </div>
                          
                          {sprintInfiniteData?.[sprintInfiniteData.length - 1]?.pagination?.total > selectedSprintTickets.length && (
                            <div ref={sprintLoadMoreRef} className="py-12 flex flex-col items-center justify-center gap-3 opacity-50">
                              <div className="flex items-center gap-2">
                                <Loader2 className="w-5 h-5 text-primary animate-spin" />
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('loadingMore')}</span>
                              </div>
                              <div className="text-[8px] font-bold text-slate-600 uppercase tracking-widest animate-pulse">
                                {t('page')} {sprintSize} / {Math.ceil(sprintInfiniteData[0]?.pagination?.total / 10)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-black/20 rounded-[32px] border border-white/5 border-dashed">
                        <div className="w-16 h-16 bg-slate-500/10 rounded-3xl flex items-center justify-center text-slate-500 mb-4 font-black">?</div>
                        <h3 className="text-xl font-black text-white mb-2">{t('noSprintSelected')}</h3>
                        <p className="text-slate-500 text-sm max-w-xs">{t('selectSprintToView')}</p>
                      </div>
                    )}
                  </motion.div>

                  {/* Backlog View Layer */}
                  <motion.div 
                    key="backlog-view"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ 
                      opacity: activeTab === 'backlog' ? 1 : 0,
                      x: activeTab === 'backlog' ? 0 : -20,
                      pointerEvents: activeTab === 'backlog' ? 'auto' : 'none',
                      visibility: activeTab === 'backlog' ? 'visible' : 'hidden'
                    }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    className="absolute inset-0 flex flex-col min-h-0"
                  >
                    <div className="flex-1 flex flex-col min-h-0 relative">
                      <div 
                        ref={listRef}
                        className="flex-1 overflow-y-auto custom-scrollbar pr-3 relative"
                      >
                        <div
                          style={{
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            width: '100%',
                            position: 'relative',
                            minHeight: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          {(isLoadingTickets || (swrValidating && !allTickets.length)) ? (
                            Array.from({ length: 5 }).map((_, i) => (
                              <div key={`skeleton-backlog-${i}`} style={{ transform: `translateY(${i * 84}px)`, position: 'absolute', width: '100%' }}>
                                <TicketSkeleton />
                              </div>
                            ))
                          ) : (
                            <>
                              {rowVirtualizer.getVirtualItems().length === 0 && !swrValidating && (
                                <div className="flex-1 flex flex-col items-center justify-center p-12 text-center animate-in fade-in zoom-in-95 duration-700">
                                  {searchQuery && (
                                    <div className="relative mb-8">
                                       <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full" />
                                       <div className="relative w-32 h-32 bg-primary/10 rounded-[40px] border border-primary/20 flex items-center justify-center transform -rotate-12 group-hover:rotate-0 transition-all duration-700">
                                         <Search size={64} className="text-primary/40" strokeWidth={1} />
                                       </div>
                                    </div>
                                  )}
                                  {!searchQuery && (
                                    <div className="relative mb-8">
                                      <div className="absolute inset-0 bg-emerald-500/20 blur-3xl rounded-full" />
                                      <div className="relative w-32 h-32 bg-emerald-500/10 rounded-[40px] border border-emerald-500/20 flex items-center justify-center transform rotate-12 group-hover:rotate-0 transition-all duration-700">
                                        <CheckCircle2 size={64} className="text-emerald-500" strokeWidth={1.5} />
                                        <motion.div 
                                          initial={{ scale: 0 }}
                                          animate={{ scale: 1 }}
                                          transition={{ delay: 0.5, type: "spring" }}
                                          className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full border-4 border-[#0a0f18] flex items-center justify-center text-white"
                                        >
                                          <CheckCircle2 size={16} />
                                        </motion.div>
                                      </div>
                                    </div>
                                  )}
                                  <h3 className="text-2xl font-black text-white mb-3 italic tracking-tight">
                                    {searchQuery ? t('noResultsTitle') : t('emptyBacklogTitle')}
                                  </h3>
                                </div>
                              )}

                              {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                                <TicketRow
                                  key={allTickets[virtualRow.index]?._id || virtualRow.key}
                                  ticket={allTickets[virtualRow.index]}
                                  onClick={setSelectedTicket}
                                  onDelete={handleDeleteTicket}
                                  onAction={(selectedSprintId && (selectedSprint?.status === 'draft' || selectedSprint?.status === 'planned')) ? addToSprint : null}
                                  actionIcon={Plus}
                                  isSelected={selectedTicket?._id === allTickets[virtualRow.index]?._id}
                                  t={t}
                                  lang={lang}
                                  virtualRow={virtualRow}
                                />
                              ))}
                            </>
                          )}
                        </div>

                        {infiniteData?.[infiniteData.length - 1]?.pagination?.total > filteredBacklog.length && (
                          <div ref={loadMoreRef} className="py-12 flex flex-col items-center justify-center gap-3 opacity-50">
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-5 h-5 text-primary animate-spin" />
                              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('loadingMore')}</span>
                            </div>
                            <div className="text-[8px] font-bold text-slate-600 uppercase tracking-widest animate-pulse">
                              {t('page')} {size} / {Math.ceil(infiniteData[0]?.pagination?.total / 10)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      {createPortal(
        <AnimatePresence mode='wait'>
          {selectedTicket && (
            <TicketModal 
              key="backlog-ticket-modal"
              ticket={selectedTicket} 
              onClose={() => setSelectedTicket(null)} 
              onUpdate={() => {
                mutateTickets();
                setSelectedTicket(null);
              }}
              onAddToSprint={selectedSprintId && selectedSprint && (selectedSprint.status === 'planned' || selectedSprint.status === 'draft') ? (id) => {
                addToSprint(id);
                setSelectedTicket(null);
              } : null}
              activeSprint={selectedSprintId ? selectedSprint : null}
              t={t} 
            />
          )}
        </AnimatePresence>,
        document.body
      )}
      
      <ConfirmationModal {...confModal} onClose={() => setConfModal({ ...confModal, isOpen: false })} t={t} />

      <SyncModal 
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        onSync={handleSyncToExternal}
        isSyncing={isSyncing}
        hasJira={hasJira}
        hasAzure={hasAzure}
        t={t}
      />

      {createPortal(
        <AnimatePresence>
          {isCreateModalOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
              <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-[#111827] border border-white/10 rounded-[40px] w-full max-w-md p-10 pt-16 cursor-default shadow-3xl text-center relative" onClick={(e) => e.stopPropagation()}>
                <button 
                  onClick={() => setIsCreateModalOpen(false)} 
                  className="absolute top-6 right-6 p-2 rounded-xl bg-white/5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/20 transition-all border border-white/10"
                >
                  <X size={18} />
                </button>
                <div className="w-16 h-16 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-6"><Plus size={32} className="text-primary" /></div>
                <h2 className="text-3xl font-black text-white mb-2">{t('createSprint')}</h2>
                <p className="text-slate-500 text-sm mb-8">{t('newSprintDescription')}</p>
                <form onSubmit={handleCreateSprint} className="space-y-6 text-left">
                  <input type="text" required value={newSprint.name} onChange={(e) => setNewSprint({ ...newSprint, name: e.target.value })} placeholder={t('sprintPlaceholder')} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white text-sm focus:border-primary transition-all" />
                  
                  <AnimatePresence>
                    {newSprint.name.trim().length > 0 && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-6">
                        <div className="space-y-3">
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-1">{t('sprintDuration')}</p>
                          <div className="relative">
                            <input type="number" required min="1" value={newSprint.duration} onChange={(e) => setNewSprint({ ...newSprint, duration: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white text-sm focus:border-primary transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                            {newSprint.duration && (
                              <div className="absolute right-4 top-1/2 -translate-y-1/2 px-3 py-1 bg-primary/10 border border-primary/20 rounded-lg animate-in fade-in zoom-in duration-300">
                                <span className="text-[9px] font-black text-primary uppercase tracking-widest">
                                  {Math.round(parseInt(newSprint.duration || 0) * POINTS_PER_DAY)} {t('capacity')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {newSprint.duration && parseInt(newSprint.duration) > 0 && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-6">
                            <div className="space-y-3">
                              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-1">{t('startDate')}</p>
                              <CustomDatePicker value={newSprint.startDate} lang={lang} t={t} duration={newSprint.duration} sprints={sprints} onChange={(date) => setNewSprint({ ...newSprint, startDate: date })} />
                            </div>
                            
                            {newSprint.startDate && (
                              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                                <div className="px-4 py-3 bg-primary/5 border border-primary/10 rounded-2xl flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
                                  <span className="text-slate-500">{t('endDate')}</span>
                                  <span className="text-primary">{(() => {
                                    if (!newSprint.startDate) return '';
                                    const d = new Date(newSprint.startDate);
                                    d.setDate(d.getDate() + parseInt(newSprint.duration || 0));
                                    return d.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });
                                  })()}</span>
                                </div>
                                
                                <div className="flex gap-4 pt-4">
                                  <button type="button" onClick={() => setIsCreateModalOpen(false)} className="flex-1 px-6 py-4 rounded-2xl font-bold text-slate-400 hover:bg-white/5 transition-all">{t('cancel')}</button>
                                  <button type="submit" disabled={isCreating} className="flex-1 px-6 py-4 bg-primary text-white rounded-2xl font-bold hover:bg-blue-600 transition-all shadow-lg shadow-primary/20">{isCreating ? t('loading') : t('createSprint')}</button>
                                </div>
                              </motion.div>
                            )}
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {createPortal(
        <FilterModal 
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          t={t}
          // Visibility Controls
          showPriority={true}
          showDateRange={false}
          showStatus={activeTab === 'sprint'}
          showPlanning={activeTab === 'backlog'}
          // Selection Values
          selectedPriority={activeTab === 'backlog' ? backlogFilters.priority : sprintFilters.priority}
          selectedStatus={activeTab === 'sprint' ? sprintFilters.status : 'all'}
          selectedPlanning={activeTab === 'backlog' ? backlogFilters.planning : 'all'}
          onApply={(updated) => {
            if (activeTab === 'backlog') {
              setBacklogFilters(prev => ({ ...prev, ...updated }));
            } else {
              setSprintFilters(prev => ({ ...prev, ...updated }));
            }
          }}
        />,
        document.body
      )}
    </>
  );
};

export default Backlog;
